import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchAdminApplication,
  updateAdminApplication,
  deleteAdminApplication,
  convertAdminApplication,
  fetchAdminApplicationDocumentDownloadUrl,
  fetchAdminAuditLogs,
} from "../../api/adminData";
import {
  AdminAuditTimeline,
  AdminAuditTimelineEntry,
} from "../../components/admin/AuditLogVisual";
import { formatDateOnly, formatDateTime } from "../../utils/datetime";
import { formatLanguages } from "../../constants/languages";
import { formatApplicationSource } from "../../constants/applicationSource";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatAuditLog, auditActorDisplay } from "../../utils/auditDisplay";
import { getApplicationStatusMeta } from "./AdminApplicationsPage";
import ApplicationNotesBlock from "./application/ApplicationNotesBlock";
import EntityTasksTab from "./tasks/EntityTasksTab";
import EntityWorkReportsTab from "./tasks/EntityWorkReportsTab";
import EntityActivityFeed from "./activity/EntityActivityFeed";

const STATUS_OPTIONS = [
  { value: "new", label: "Neu" },
  { value: "reviewing", label: "In Prüfung" },
  { value: "viewing_scheduled", label: "Besichtigung geplant" },
  { value: "accepted", label: "Angenommen" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "archived", label: "Archiviert" },
];

const SALUTATION_OPTIONS = [
  { value: "", label: "—" },
  { value: "mr", label: "Herr" },
  { value: "ms", label: "Frau" },
  { value: "diverse", label: "Divers" },
  { value: "none", label: "Keine Angabe" },
];
const SALUTATION_LABEL = { mr: "Herr", ms: "Frau", diverse: "Divers", none: "Keine Angabe", other: "Andere" };

const APPLICANT_TYPE_OPTIONS = [
  { value: "person", label: "Privatperson" },
  { value: "company", label: "Firma" },
];

const TRIBOOL_OPTIONS = [
  { value: "", label: "—" },
  { value: "true", label: "Ja" },
  { value: "false", label: "Nein" },
];

const OCCUPANT_COUNT_OPTIONS = [
  { value: "", label: "—" },
  { value: "1", label: "1 Person" },
  { value: "2", label: "2 Personen" },
  { value: "3", label: "3 Personen" },
  { value: "4+", label: "4+ Personen" },
];

const BOOKING_FOR_OPTIONS = [
  { value: "", label: "—" },
  { value: "employee", label: "Mitarbeiter" },
  { value: "expat", label: "Expat" },
  { value: "consultant", label: "Consultant / Freelancer" },
  { value: "guest", label: "Gast / temporärer Mitarbeiter" },
  { value: "other", label: "Andere" },
];

function fmtBool(v) {
  if (v === true) return "Ja";
  if (v === false) return "Nein";
  return "—";
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function sizeKb(bytes) {
  if (!bytes) return "—";
  return `${Math.round(Number(bytes) / 1024)} KB`;
}

// ── Form helpers ──────────────────────────────────────────────────────────

// All editable keys on the form. Must mirror the backend ApplicationUpdate schema.
const EDITABLE_KEYS = [
  "applicant_type",
  "salutation",
  "first_name",
  "last_name",
  "email",
  "phone",
  "birth_date",
  "nationality",
  "residence_permit",
  "address_street",
  "address_zip",
  "address_city",
  "company_name",
  "company_registration_no",
  "company_position",
  "contact_person_name",
  "occupant_name",
  "occupant_count",
  "booking_for",
  "occupation",
  "occupation_other",
  "employer",
  "gross_income",
  "work_style",
  "move_in_date",
  "rent_duration",
  "debt_collections",
  "open_collections",
  "loss_certificates",
  "financial_notes",
  "wg_experience",
  "wg_experience_duration",
  "day_rhythm",
  "social_style",
  "smoker",
  "cleanliness",
  "pets",
  "pets_details",
  "visitors",
  "wg_rules_accepted",
  "self_description",
  "motivation_room",
  "motivation_wg",
  "credit_check_consent",
  "reference_check_consent",
  "status",
];

const BOOL_KEYS = new Set([
  "wg_rules_accepted",
  "credit_check_consent",
  "reference_check_consent",
]);

const TRIBOOL_KEYS = new Set([
  "debt_collections",
  "open_collections",
  "loss_certificates",
  "wg_experience",
  "smoker",
  "pets",
]);

const DATE_KEYS = new Set(["birth_date", "move_in_date"]);

function toFormState(app) {
  const out = {};
  for (const k of EDITABLE_KEYS) {
    const raw = app?.[k];
    if (BOOL_KEYS.has(k)) {
      out[k] = raw === true;
    } else if (TRIBOOL_KEYS.has(k)) {
      out[k] = raw === true ? "true" : raw === false ? "false" : "";
    } else if (DATE_KEYS.has(k)) {
      // keep ISO yyyy-mm-dd for input[type=date]; API returns yyyy-mm-dd already.
      out[k] = raw ? String(raw).slice(0, 10) : "";
    } else {
      out[k] = raw == null ? "" : String(raw);
    }
  }
  return out;
}

function buildUpdatePayload(form, baseline) {
  // Only include changed fields in the PUT body (exclude_unset on the server).
  const body = {};
  for (const k of EDITABLE_KEYS) {
    const f = form[k];
    const b = baseline[k];
    if (f === b) continue;

    if (BOOL_KEYS.has(k)) {
      body[k] = Boolean(f);
    } else if (TRIBOOL_KEYS.has(k)) {
      body[k] = f === "true" ? true : f === "false" ? false : null;
    } else if (DATE_KEYS.has(k)) {
      body[k] = f ? f : null;
    } else {
      const trimmed = typeof f === "string" ? f.trim() : f;
      body[k] = trimmed === "" ? null : trimmed;
    }
  }
  return body;
}

// ── Small presentational helpers ──────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="mb-4 rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
      <h3 className="mb-4 mt-0 text-[9px] font-bold uppercase tracking-[1px] text-slate-500 dark:text-slate-400">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        rowGap: "14px",
        columnGap: "16px",
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {children}
    </div>
  );
}

function RO({ value, pre = false }) {
  return (
    <div
      className={`text-sm text-slate-800 dark:text-white ${
        pre ? "whitespace-pre-wrap" : ""
      }`}
    >
      {value == null || value === "" ? "—" : value}
    </div>
  );
}

const inputClass =
  "h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-[#FF7A3D] dark:border-white/10 dark:bg-[#1a2030] dark:text-white";
const textareaClass =
  "min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#FF7A3D] dark:border-white/10 dark:bg-[#1a2030] dark:text-white";

function TextField({ form, setForm, k, type = "text" }) {
  return (
    <input
      className={inputClass}
      type={type}
      value={form[k] ?? ""}
      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
    />
  );
}

function TextAreaField({ form, setForm, k }) {
  return (
    <textarea
      className={textareaClass}
      value={form[k] ?? ""}
      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
    />
  );
}

function SelectField({ form, setForm, k, options }) {
  return (
    <select
      className={inputClass}
      value={form[k] ?? ""}
      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function CheckField({ form, setForm, k, label }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-800 dark:text-white">
      <input
        type="checkbox"
        checked={Boolean(form[k])}
        onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.checked }))}
      />
      {label}
    </label>
  );
}

const occupantCountLabel = (val) =>
  OCCUPANT_COUNT_OPTIONS.find((o) => o.value === val)?.label || "—";

const bookingForLabel = (val) =>
  BOOKING_FOR_OPTIONS.find((o) => o.value === val)?.label || "—";

function displayHeaderName(app) {
  const contact = `${app.first_name || ""} ${app.last_name || ""}`.trim();
  if (app.applicant_type === "company" && app.company_name) {
    return contact ? `${app.company_name} (${contact})` : app.company_name;
  }
  return contact || "—";
}

function docCategoryLabel(category) {
  if (category === "generated_form") return "Bewerbungsformular (PDF)";
  if (category === "upload") return "Upload";
  return category || "Dokument";
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function AdminApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const baselineRef = useRef({});
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  // Audit history
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");

  const loadAudit = useCallback(async (appId) => {
    setAuditLoading(true);
    setAuditError("");
    try {
      const res = await fetchAdminAuditLogs({
        entity_type: "application",
        entity_id: appId,
      });
      setAuditLogs(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setAuditError(e?.message || "Verlauf konnte nicht geladen werden.");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminApplication(id);
      setApp(data);
      const nextForm = toFormState(data);
      setForm(nextForm);
      baselineRef.current = nextForm;
      loadAudit(id);
    } catch (e) {
      setError(e?.message || "Bewerbung konnte nicht geladen werden.");
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [id, loadAudit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStartEdit = () => {
    if (!app) return;
    const next = toFormState(app);
    setForm(next);
    baselineRef.current = next;
    setIsEditing(true);
  };

  const handleCancel = () => {
    setForm(baselineRef.current);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!app) return;
    const body = buildUpdatePayload(form, baselineRef.current);
    if (Object.keys(body).length === 0) {
      setIsEditing(false);
      toast.info("Keine Änderungen.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateAdminApplication(app.id, body);
      setApp(updated);
      const next = toFormState(updated);
      setForm(next);
      baselineRef.current = next;
      setIsEditing(false);
      toast.success("Bewerbung gespeichert.");
      loadAudit(app.id);
    } catch (e) {
      toast.error(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertDialogOpenChange = (next) => {
    if (converting) return;
    setConvertDialogOpen(next);
  };

  const handleConvert = async () => {
    if (!app || converting) return;
    setConverting(true);
    try {
      const result = await convertAdminApplication(app.id);
      const updatedApp = result.application;
      const mergedApp = { ...app, ...updatedApp };
      setApp(mergedApp);
      const next = toFormState(mergedApp);
      setForm(next);
      baselineRef.current = next;
      const tenantId = String(result.tenant?.id || "");
      toast.success(
        `Bewerber als Mieter angelegt (ID ${tenantId.slice(0, 8)}).`
      );
      setConvertDialogOpen(false);
      loadAudit(app.id);
    } catch (e) {
      toast.error(e?.message || "Konvertierung fehlgeschlagen.");
    } finally {
      setConverting(false);
    }
  };

  async function handleOpenDocument(doc) {
    try {
      const data = await fetchAdminApplicationDocumentDownloadUrl(app.id, doc.id);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Keine Download-URL erhalten.");
      }
    } catch (e) {
      toast.error(e?.message || "Download konnte nicht gestartet werden.");
    }
  }

  const auditResolvers = useMemo(() => ({}), []);

  if (loading) {
    return (
      <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Lade…</div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <Link to="/admin/applications" className="text-sm text-[#FF7A3D] no-underline">
          ← Zurück zur Liste
        </Link>
        <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }
  if (!app) return null;

  const meta = getApplicationStatusMeta(app.status);
  const effectiveType = isEditing ? form.applicant_type : app.applicant_type;
  const isCompany = effectiveType === "company";
  const documents = Array.isArray(app.documents) ? app.documents : [];
  const generated = documents.filter((d) => d.category === "generated_form");
  const uploads = documents.filter((d) => d.category !== "generated_form");

  return (
    <div className="p-6">
      <Link to="/admin/applications" className="text-sm text-[#FF7A3D] no-underline">
        ← Zurück zur Liste
      </Link>

      {/* Kopf */}
      <div className="mb-4 mt-3 rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {app.applicant_type === "company" ? "Firma" : "Privatperson"} · Bewerbung #
              {app.id.slice(0, 8)}
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-800 dark:text-white">
              {displayHeaderName(app)}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span className={meta.pillClass}>{meta.label}</span>
              <span>·</span>
              <span>Eingang: {formatDateTime(app.created_at)}</span>
              <span>·</span>
              <span>
                Inserat:{" "}
                {app.listing ? (
                  <Link
                    to={`/admin/listings`}
                    className="text-[#FF7A3D] no-underline"
                  >
                    {app.listing.unit_short_id || "—"}
                    {" · "}
                    {app.listing.room_name
                      ? `Room ${app.listing.room_name}`
                      : "Ganze Unit"}
                    {app.listing.title ? ` — ${app.listing.title}` : ""}
                  </Link>
                ) : (
                  <span className="text-slate-500 dark:text-slate-400">
                    — · Inserat gelöscht
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isEditing ? (
              <>
                {app.status !== "converted" && (
                  <button
                    onClick={handleStartEdit}
                    className="rounded-md border border-[#FF7A3D] bg-[#FF7A3D] px-4 py-2 text-xs font-semibold text-white hover:opacity-90"
                  >
                    Bearbeiten
                  </button>
                )}
                {app.status === "accepted" && (
                  <button
                    onClick={() => setConvertDialogOpen(true)}
                    className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/15"
                  >
                    Als Mieter übernehmen
                  </button>
                )}
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-[#f87171] dark:hover:bg-red-500/15"
                >
                  Löschen
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-500 dark:border-white/10 dark:bg-[#141824] dark:text-slate-200"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-md border border-[#FF7A3D] bg-[#FF7A3D] px-4 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {saving ? "Speichere…" : "Speichern"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status */}
      <Section title="Status">
        <Grid>
          <Label>Status</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="status"
              options={STATUS_OPTIONS}
            />
          ) : (
            <div>
              <span className={meta.pillClass}>{meta.label}</span>
            </div>
          )}
          <Label>Bewerbertyp</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="applicant_type"
              options={APPLICANT_TYPE_OPTIONS}
            />
          ) : (
            <RO value={isCompany ? "Firma" : "Privatperson"} />
          )}
        </Grid>
      </Section>

      {/* Person oder Firma */}
      {isCompany ? (
        <Section title="Firma">
          <Grid>
            <Label>Firmenname</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="company_name" />
            ) : (
              <RO value={fmt(app.company_name)} />
            )}
            <Label>HR-/UID-Nummer</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="company_registration_no" />
            ) : (
              <RO value={fmt(app.company_registration_no)} />
            )}
            <Label>Position Kontaktperson</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="company_position" />
            ) : (
              <RO value={fmt(app.company_position)} />
            )}
            <Label>Kontaktperson</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="contact_person_name" />
            ) : (
              <RO value={fmt(app.contact_person_name)} />
            )}
            <Label>Anrede</Label>
            {isEditing ? (
              <SelectField
                form={form}
                setForm={setForm}
                k="salutation"
                options={SALUTATION_OPTIONS}
              />
            ) : (
              <RO value={fmt(SALUTATION_LABEL[app.salutation])} />
            )}
            <Label>Kontakt Vorname</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="first_name" />
            ) : (
              <RO value={fmt(app.first_name)} />
            )}
            <Label>Kontakt Nachname</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="last_name" />
            ) : (
              <RO value={fmt(app.last_name)} />
            )}
            <Label>E-Mail</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="email" type="email" />
            ) : (
              <RO value={fmt(app.email)} />
            )}
            <Label>Telefon</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="phone" />
            ) : (
              <RO value={fmt(app.phone)} />
            )}
            <Label>Firmenadresse</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="address_street" />
            ) : (
              <RO value={fmt(app.address_street)} />
            )}
            <Label>PLZ / Ort</Label>
            {isEditing ? (
              <div className="flex gap-2">
                <input
                  className={`${inputClass} max-w-[120px]`}
                  value={form.address_zip || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_zip: e.target.value }))
                  }
                />
                <input
                  className={inputClass}
                  value={form.address_city || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_city: e.target.value }))
                  }
                />
              </div>
            ) : (
              <RO value={`${fmt(app.address_zip)} ${fmt(app.address_city)}`.trim()} />
            )}
          </Grid>
        </Section>
      ) : (
        <Section title="Persönliche Angaben">
          <Grid>
            <Label>Anrede</Label>
            {isEditing ? (
              <SelectField
                form={form}
                setForm={setForm}
                k="salutation"
                options={SALUTATION_OPTIONS}
              />
            ) : (
              <RO value={fmt(SALUTATION_LABEL[app.salutation])} />
            )}
            <Label>Vorname</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="first_name" />
            ) : (
              <RO value={fmt(app.first_name)} />
            )}
            <Label>Nachname</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="last_name" />
            ) : (
              <RO value={fmt(app.last_name)} />
            )}
            <Label>E-Mail</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="email" type="email" />
            ) : (
              <RO value={fmt(app.email)} />
            )}
            <Label>Telefon</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="phone" />
            ) : (
              <RO value={fmt(app.phone)} />
            )}
            <Label>Geburtsdatum</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="birth_date" type="date" />
            ) : (
              <RO value={formatDateOnly(app.birth_date)} />
            )}
            <Label>Nationalität</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="nationality" />
            ) : (
              <RO value={fmt(app.nationality)} />
            )}
            <Label>Aufenthaltsbewilligung</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="residence_permit" />
            ) : (
              <RO value={fmt(app.residence_permit)} />
            )}
            <Label>Strasse</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="address_street" />
            ) : (
              <RO value={fmt(app.address_street)} />
            )}
            <Label>PLZ / Ort</Label>
            {isEditing ? (
              <div className="flex gap-2">
                <input
                  className={`${inputClass} max-w-[120px]`}
                  value={form.address_zip || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_zip: e.target.value }))
                  }
                />
                <input
                  className={inputClass}
                  value={form.address_city || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_city: e.target.value }))
                  }
                />
              </div>
            ) : (
              <RO value={`${fmt(app.address_zip)} ${fmt(app.address_city)}`.trim()} />
            )}
          </Grid>
        </Section>
      )}

      <Section title="Sprachen & Quelle">
        <Grid>
          <Label>Sprachen</Label>
          <RO value={formatLanguages(app.languages)} />
          <Label>Wie gefunden?</Label>
          <RO value={formatApplicationSource(app.source, app.source_other)} />
        </Grid>
      </Section>

      {isCompany && (
        <Section title="Bewohner-Info">
          <Grid>
            <Label>Name der einziehenden Person</Label>
            {isEditing ? (
              <TextField form={form} setForm={setForm} k="occupant_name" />
            ) : (
              <RO value={fmt(app.occupant_name)} />
            )}
            <Label>Anzahl Personen</Label>
            {isEditing ? (
              <SelectField
                form={form}
                setForm={setForm}
                k="occupant_count"
                options={OCCUPANT_COUNT_OPTIONS}
              />
            ) : (
              <RO value={occupantCountLabel(app.occupant_count)} />
            )}
            <Label>Gebucht für</Label>
            {isEditing ? (
              <SelectField
                form={form}
                setForm={setForm}
                k="booking_for"
                options={BOOKING_FOR_OPTIONS}
              />
            ) : (
              <RO value={bookingForLabel(app.booking_for)} />
            )}
          </Grid>
        </Section>
      )}

      {!isCompany && (
      <Section title="Beruf & Wohnwunsch">
        <Grid>
          <Label>Beschäftigung</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="occupation" />
          ) : (
            <RO value={fmt(app.occupation)} />
          )}
          <Label>Beschäftigung (Sonstiges)</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="occupation_other" />
          ) : (
            <RO value={fmt(app.occupation_other)} />
          )}
          <Label>Arbeitgeber</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="employer" />
          ) : (
            <RO value={fmt(app.employer)} />
          )}
          <Label>Bruttoeinkommen</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="gross_income" />
          ) : (
            <RO value={fmt(app.gross_income)} />
          )}
          <Label>Arbeitsweise</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="work_style" />
          ) : (
            <RO value={fmt(app.work_style)} />
          )}
          <Label>Einzugsdatum</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="move_in_date" type="date" />
          ) : (
            <RO value={formatDateOnly(app.move_in_date)} />
          )}
          <Label>Mietdauer</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="rent_duration" />
          ) : (
            <RO value={fmt(app.rent_duration)} />
          )}
        </Grid>
      </Section>
      )}

      {!isCompany && (
      <Section title="Finanzielle Selbstauskunft">
        <Grid>
          <Label>Betreibungen (je gehabt)</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="debt_collections"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.debt_collections)} />
          )}
          <Label>Offene Betreibungen</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="open_collections"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.open_collections)} />
          )}
          <Label>Verlustscheine</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="loss_certificates"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.loss_certificates)} />
          )}
          <Label>Anmerkungen</Label>
          {isEditing ? (
            <TextAreaField form={form} setForm={setForm} k="financial_notes" />
          ) : (
            <RO value={fmt(app.financial_notes)} pre />
          )}
        </Grid>
      </Section>
      )}

      {!isCompany && (
      <Section title="WG-Kompatibilität">
        <Grid>
          <Label>WG-Erfahrung</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="wg_experience"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.wg_experience)} />
          )}
          <Label>Erfahrungsdauer</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="wg_experience_duration" />
          ) : (
            <RO value={fmt(app.wg_experience_duration)} />
          )}
          <Label>Tagesrhythmus</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="day_rhythm" />
          ) : (
            <RO value={fmt(app.day_rhythm)} />
          )}
          <Label>Sozialer Stil</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="social_style" />
          ) : (
            <RO value={fmt(app.social_style)} />
          )}
          <Label>Raucher</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="smoker"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.smoker)} />
          )}
          <Label>Ordentlichkeit</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="cleanliness" />
          ) : (
            <RO value={fmt(app.cleanliness)} />
          )}
          <Label>Haustiere</Label>
          {isEditing ? (
            <SelectField
              form={form}
              setForm={setForm}
              k="pets"
              options={TRIBOOL_OPTIONS}
            />
          ) : (
            <RO value={fmtBool(app.pets)} />
          )}
          <Label>Haustier-Details</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="pets_details" />
          ) : (
            <RO value={fmt(app.pets_details)} />
          )}
          <Label>Besuch</Label>
          {isEditing ? (
            <TextField form={form} setForm={setForm} k="visitors" />
          ) : (
            <RO value={fmt(app.visitors)} />
          )}
          <Label>WG-Regeln akzeptiert</Label>
          {isEditing ? (
            <CheckField
              form={form}
              setForm={setForm}
              k="wg_rules_accepted"
              label="Ja"
            />
          ) : (
            <RO value={fmtBool(app.wg_rules_accepted)} />
          )}
          <Label>Selbstbeschreibung</Label>
          {isEditing ? (
            <TextAreaField form={form} setForm={setForm} k="self_description" />
          ) : (
            <RO value={fmt(app.self_description)} pre />
          )}
          <Label>Motivation Zimmer</Label>
          {isEditing ? (
            <TextAreaField form={form} setForm={setForm} k="motivation_room" />
          ) : (
            <RO value={fmt(app.motivation_room)} pre />
          )}
          <Label>Motivation WG</Label>
          {isEditing ? (
            <TextAreaField form={form} setForm={setForm} k="motivation_wg" />
          ) : (
            <RO value={fmt(app.motivation_wg)} pre />
          )}
        </Grid>
      </Section>
      )}

      <Section title="Einwilligungen & Meta">
        <Grid>
          <Label>Bonitätsprüfung erlaubt</Label>
          {isEditing ? (
            <CheckField
              form={form}
              setForm={setForm}
              k="credit_check_consent"
              label="Ja"
            />
          ) : (
            <RO value={fmtBool(app.credit_check_consent)} />
          )}
          <Label>Referenzauskünfte erlaubt</Label>
          {isEditing ? (
            <CheckField
              form={form}
              setForm={setForm}
              k="reference_check_consent"
              label="Ja"
            />
          ) : (
            <RO value={fmtBool(app.reference_check_consent)} />
          )}
          {/* Read-only, never editable — legal evidence */}
          <Label>Angaben bestätigt</Label>
          <RO value={fmtBool(app.confirm_correct)} />
          <Label>Datenschutz akzeptiert</Label>
          <RO value={fmtBool(app.privacy_accepted)} />
          <Label>Submitted IP</Label>
          <RO value={fmt(app.submitted_ip)} />
          <Label>User-Agent</Label>
          <RO value={fmt(app.submitted_user_agent)} pre />
          <Label>Eingereicht am</Label>
          <RO value={formatDateTime(app.created_at)} />
          <Label>Einwilligung erteilt am</Label>
          <RO value={formatDateTime(app.consent_given_at)} />
          <Label>E-Mail versendet</Label>
          <RO
            value={
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  app.email_sent
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                }`}
              >
                {app.email_sent ? "Gesendet" : "Ausstehend"}
              </span>
            }
          />
          <Label>PDF Object Key</Label>
          <RO value={fmt(app.pdf_object_key)} />
        </Grid>
      </Section>

      <ApplicationNotesBlock applicationId={app.id} />

      <EntityTasksTab filter={{ application_id: app.id }} title="Aufgaben" />

      {/* D2d — this page has no tab bar, so the reports live as a section
          directly after Aufgaben: both answer "what work happened for
          this application". */}
      <EntityWorkReportsTab scope="application" entityId={app.id} />

      {/* D3: no tab bar on this page, so the timeline arrives as a
          section next to Notizen, Aufgaben and Rapporte. */}
      <EntityActivityFeed
        entityType="application"
        entityId={app.id}
        headingId="application-activity-heading"
      />

      <Section title="Dokumente">
        {generated.length === 0 && uploads.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Keine Dokumente vorhanden.
          </div>
        ) : (
          <div className="space-y-2">
            {generated.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[rgba(255,122,61,0.35)] bg-[rgba(255,122,61,0.08)] px-3 py-2"
              >
                <div>
                  <div className="text-sm font-semibold text-[#FF7A3D]">
                    {docCategoryLabel(d.category)}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {d.file_name} · {sizeKb(d.file_size)} · {formatDateOnly(d.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleOpenDocument(d)}
                  className="rounded-md border border-[#FF7A3D] bg-[#FF7A3D] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                >
                  Öffnen
                </button>
              </div>
            ))}
            {uploads.length > 0 && (
              <div className="pt-2">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Bewerber-Uploads
                </div>
                <div className="space-y-2">
                  {uploads.map((d) => (
                    <div
                      key={d.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-black/10 bg-white px-3 py-2 dark:border-white/[0.07] dark:bg-[#1a2030]"
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-800 dark:text-white">
                          {d.file_name}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {d.mime_type || "—"} · {sizeKb(d.file_size)} ·{" "}
                          {formatDateOnly(d.created_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleOpenDocument(d)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#FF7A3D] hover:text-[#FF7A3D] dark:border-white/10 dark:bg-[#141824] dark:text-slate-200"
                      >
                        Öffnen
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title="Änderungsverlauf">
        {auditLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Lade Verlauf…
          </div>
        ) : auditError ? (
          <div className="text-sm text-red-600 dark:text-red-400">{auditError}</div>
        ) : auditLogs.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Keine Aktivitäten vorhanden.
          </div>
        ) : (
          <AdminAuditTimeline>
            {auditLogs.map((entry) => {
              const { summary, changes } = formatAuditLog(entry, {
                entityType: "application",
                resolvers: auditResolvers,
              });
              return (
                <AdminAuditTimelineEntry
                  key={entry.id}
                  summary={summary}
                  changes={changes}
                  createdAt={entry.created_at}
                  actor={auditActorDisplay(entry)}
                  action={entry.action}
                />
              );
            })}
          </AdminAuditTimeline>
        )}
      </Section>

      {deleteModalOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
          onClick={() => !deleting && setDeleteModalOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-[14px] border border-black/10 bg-white p-6 shadow-lg dark:border-white/[0.07] dark:bg-[#141824]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-application-title"
          >
            <h2
              id="delete-application-title"
              className="mb-3 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]"
            >
              Bewerbung löschen?
            </h2>
            <p className="mb-6 text-sm text-[#64748b] dark:text-[#6b7a9a]">
              Bewerbung von {displayHeaderName(app)}
              {app.listing && app.listing.title ? ` für "${app.listing.title}"` : ""}
              {" "}wirklich löschen? Diese Aktion kann nur manuell von einem Admin
              rückgängig gemacht werden.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteModalOpen(false)}
                className="rounded-[8px] border border-black/10 bg-transparent px-4 py-2 text-sm font-semibold text-[#64748b] hover:bg-slate-100 disabled:opacity-50 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => {
                  setDeleting(true);
                  deleteAdminApplication(app.id)
                    .then(() => {
                      toast.success("Bewerbung gelöscht.");
                      setDeleteModalOpen(false);
                      navigate("/admin/applications", { replace: true });
                    })
                    .catch((e) => {
                      toast.error(e.message || "Löschen fehlgeschlagen.");
                    })
                    .finally(() => setDeleting(false));
                }}
                className="rounded-[8px] border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-500/20 dark:bg-red-500/10 dark:text-[#f87171] dark:hover:bg-red-500/15"
              >
                {deleting ? "…" : "Jetzt löschen"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={convertDialogOpen}
        onOpenChange={handleConvertDialogOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bewerbung als Mieter übernehmen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion legt einen neuen Mieter aus den Bewerbungsdaten an
              und setzt die Bewerbung auf „Konvertiert". Sie kann nicht
              rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={converting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvert}
              disabled={converting}
              className="bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-600"
            >
              {converting ? "Übernehme …" : "Als Mieter übernehmen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
